import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link } from 'react-router-dom';
import {
  roleCanEditCatalog,
  roleCanEditEvents,
  type PublicShipment,
  type PublicTrailer,
  type ShipmentListResponse,
  type ShipmentResponse,
  type TrailerListResponse,
  type TrailerResponse,
} from '@inventohub/shared';
import { HelpLink } from '../components/HelpLink';
import { ApiClientError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';

export function TrailersPage(): JSX.Element {
  const { user } = useAuth();
  const canEditTrailers = user ? roleCanEditCatalog(user.role) : false;
  const canEditShipments = user ? roleCanEditEvents(user.role) : false;
  const [trailers, setTrailers] = useState<PublicTrailer[]>([]);
  const [shipments, setShipments] = useState<PublicShipment[]>([]);
  const [trailerName, setTrailerName] = useState('');
  const [trailerNotes, setTrailerNotes] = useState('');
  const [shipmentName, setShipmentName] = useState('');
  const [shipmentNotes, setShipmentNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [trailerBody, shipmentBody] = await Promise.all([
          apiRequest<TrailerListResponse>('/api/trailers'),
          apiRequest<ShipmentListResponse>('/api/shipments'),
        ]);
        if (cancelled) return;
        setTrailers(trailerBody.trailers);
        setShipments(shipmentBody.shipments);
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(
            caught instanceof ApiClientError
              ? caught.message
              : 'Could not load trailers and shipments.',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onCreateTrailer(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      const body = await apiRequest<TrailerResponse>('/api/trailers', {
        method: 'POST',
        body: { name: trailerName, notes: trailerNotes || null },
      });
      setTrailers((current) =>
        [...current, body.trailer].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setTrailerName('');
      setTrailerNotes('');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not create the trailer.');
    }
  }

  async function onCreateShipment(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      const body = await apiRequest<ShipmentResponse>('/api/shipments', {
        method: 'POST',
        body: { name: shipmentName, notes: shipmentNotes || null },
      });
      setShipments((current) =>
        [...current, body.shipment].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setShipmentName('');
      setShipmentNotes('');
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiClientError ? caught.message : 'Could not create the shipment.',
      );
    }
  }

  return (
    <section data-testid="trailers-page">
      <div className="card">
        <div className="card__title-row">
          <h2>Trailers and shipments</h2>
          <HelpLink slug="shipped-via" />
        </div>
        <p className="meta">
          EWP trailers and named shipments. Third-party trailers are not rows in this catalog.
        </p>
        {error ? <p className="status status--error">{error}</p> : null}
      </div>
      {canEditTrailers ? (
        <form className="card" onSubmit={(event) => void onCreateTrailer(event)}>
          <h2>Add trailer</h2>
          <label className="field">
            Name
            <input
              value={trailerName}
              onChange={(event) => setTrailerName(event.target.value)}
              required
              data-testid="trailer-name"
            />
          </label>
          <label className="field">
            Notes
            <textarea
              value={trailerNotes}
              onChange={(event) => setTrailerNotes(event.target.value)}
            />
          </label>
          <button type="submit" className="button" data-testid="trailer-create">
            Create trailer
          </button>
        </form>
      ) : null}
      {canEditShipments ? (
        <form className="card" onSubmit={(event) => void onCreateShipment(event)}>
          <h2>Add shipment</h2>
          <p className="meta">
            For trips that do not get an EWP trailer, e.g. Pickup — Norman duals.
          </p>
          <label className="field">
            Name
            <input
              value={shipmentName}
              onChange={(event) => setShipmentName(event.target.value)}
              required
              data-testid="shipment-name"
            />
          </label>
          <label className="field">
            Notes
            <textarea
              value={shipmentNotes}
              onChange={(event) => setShipmentNotes(event.target.value)}
            />
          </label>
          <button type="submit" className="button" data-testid="shipment-create">
            Create shipment
          </button>
        </form>
      ) : null}
      {loading ? <p className="meta">Loading trailers and shipments…</p> : null}
      {trailers.map((trailer) => (
        <article className="card" key={trailer.id} data-testid={`trailer-card-${trailer.id}`}>
          <h2>
            <Link to={`/trailers/${trailer.id}`}>{trailer.name}</Link>
          </h2>
          <p className="meta">
            EWP trailer
            {trailer.notes ? ` · ${trailer.notes}` : trailer.active ? ' · Active' : ' · Inactive'}
          </p>
        </article>
      ))}
      {shipments.map((shipment) => (
        <article className="card" key={shipment.id} data-testid={`shipment-card-${shipment.id}`}>
          <h2>
            <Link to={`/shipments/${shipment.id}`}>{shipment.name}</Link>
          </h2>
          <p className="meta">
            Shipment
            {shipment.notes
              ? ` · ${shipment.notes}`
              : shipment.active
                ? ' · Active'
                : ' · Inactive'}
          </p>
        </article>
      ))}
    </section>
  );
}
